import React, { createContext, useState, useContext, useCallback } from 'react';

const STORAGE_KEY = 'fitsocial_mock_enabled';
const DEMO_MOCK_KEY = 'fitsocial_demo_mock';

const MockDataContext = createContext({
  isMockEnabled: false,
  toggleMock: () => {},
});

/**
 * MockDataProvider wraps the app and provides mock data toggle state.
 * Functional in development mode (via toggle) and in any environment
 * when demo mock mode is active.
 */
export const MockDataProvider = ({ children }) => {
  const isDev = process.env.NODE_ENV === 'development';

  const [isMockEnabled, setIsMockEnabled] = useState(() => {
    try {
      // Demo mock mode works in all environments
      if (localStorage.getItem(DEMO_MOCK_KEY) === 'true') return true;
      if (!isDev) return false;
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const toggleMock = useCallback(() => {
    if (!isDev) return;
    setIsMockEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // Ignore storage errors
      }
      // Reload the page so all contexts re-initialize with mock/real services
      window.location.reload();
      return next;
    });
  }, [isDev]);

  const value = {
    isMockEnabled,
    toggleMock,
  };

  return (
    <MockDataContext.Provider value={value}>
      {children}
    </MockDataContext.Provider>
  );
};

/**
 * Hook to access mock data toggle state and actions.
 * @returns {{ isMockEnabled: boolean, toggleMock: () => void }}
 */
export const useMockData = () => {
  return useContext(MockDataContext);
};

export default MockDataContext;
